import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The animated backdrop behind the console's signed-out pages — in green.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product's `screens/auth/auth-backdrop.tsx`, with the swell rebased on the Forecastify green
 * (asked for on 8 Oct 2026) so the two sign-in pages are the same page and still cannot be
 * mistaken for each other. Every guard is the product's, for the product's reasons — read its
 * header before changing any of them:
 *
 * ⚠️⚠️ **1. NO WEBGL2, NO SHADER.** jsdom has none and neither does a locked-down browser; the
 * shader mounts only after a real context has been obtained and thrown away, and the static
 * gradient underneath is the whole backdrop otherwise. The sign-in card is the one page where
 * "renders nothing" means nobody gets in.
 *
 * ⚠️⚠️ **2. `prefers-reduced-motion` GETS THE STILL GRADIENT.** A CSS rule cannot stop a
 * `requestAnimationFrame` loop.
 *
 * ⚠️ **3. `lazy`, so `ogl` stays out of the entry chunk** and the form is interactive before the
 * waves arrive.
 */

const GradientWaves = lazy(async () => import('./gradient-waves'));

/**
 * The product's Background Studio settings, with the two wave colours swapped.
 *
 * ⚠️ **`crestColor` is the logo's own green** — the dot at the foot of the mark, `#59c7c7`, sampled
 * from `forecastify-logo.png` — and `waveColor` is a deeper, greener neighbour of it, standing where
 * the product's blue stands to its violet. Everything else is the product's, so the motion, depth
 * and grain are identical and only the hue moved.
 */
const SETTINGS = {
  horizonColor: '#ffffff',
  waveColor: '#16a38c',
  crestColor: '#59c7c7',
  speed: 0.4,
  amplitude: 2.5,
  waveScale: 0.6,
  waveRatio: 1.1,
  swell: 26.5,
  turbulence: 26.5,
  tilt: 1.03,
  zoom: 1.3,
  height: 9.0,
  fogDepth: 35,
  detail: 'high',
  brightness: 1,
  opacity: 0.7,
  grain: true,
  grainIntensity: 0.05,
  mouseInteraction: false,
  parallaxStrength: 0.5,
} as const;

/** The product's two-step check: the constructor may only say no; a real context says yes. */
function canRenderWaves(): boolean {
  if (typeof document === 'undefined') return false;
  if (typeof WebGL2RenderingContext === 'undefined') return false;
  try {
    const probe = document.createElement('canvas');
    const gl = probe.getContext('webgl2');
    if (gl === null) return false;
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return true;
  } catch {
    return false;
  }
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function AuthBackdrop(): ReactNode {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    if (!canRenderWaves()) return;
    setEnabled(true);
  }, []);

  return (
    /* ⚠️ `z-0`, not `-z-10` — `packages/ui`'s `AuthFrame` says why: a negative z-index child
       paints behind its parent's own background and the first version showed nothing. */
    <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(120% 90% at 50% 100%, ${SETTINGS.crestColor} 0%, ${SETTINGS.waveColor} 38%, ${SETTINGS.horizonColor} 100%)`,
          opacity: SETTINGS.opacity,
        }}
      />
      {enabled ? (
        <Suspense fallback={null}>
          <GradientWaves {...SETTINGS} />
        </Suspense>
      ) : null}
    </div>
  );
}
